import type { MechanicDefinition } from "./types";

export const moralitySystem: MechanicDefinition = {
  id: "morality",
  disabled: true,
  name: "Morality & Alignment",
  description: "Track moral choices and their impact on character and world. Affects Director consequence weighting and NPC reactions.",
  icon: "Scale",
  category: "narrative",
  aiHints: {
    purpose: "Tracks player moral alignment to influence NPC reactions, available quests, and story consequences.",
    whenToUse: "Worlds with moral grey areas, faction-based morality, or classic good/evil alignment.",
    synergies: ["reactive_world", "reputation", "dialogue_system", "influence"],
    conflicts: [],
    prerequisites: [],
    tags: ["morality", "alignment", "ethics", "choices", "consequences"],
    complexity: 2,
    exampleConfig: { system: "grey", visible: false },
    genrePresets: {
      "classic_dnd": { system: "dual_axis", visible: true },
      "dark_fantasy": { system: "grey", visible: false },
      "star_wars": { system: "spectrum", visible: true },
      "political": { system: "faction_morality", visible: false },
    },
    stateVariables: ["alignment_label", "alignment_score", "lawful_score", "_morality_system", "_morality_visible", "_morality_shifts"],
  },
  configSchema: [
    {
      key: "system",
      label: "Morality System",
      type: "select",
      options: [
        { value: "spectrum", label: "Spectrum (good ↔ evil)", description: "A single axis from -100 (evil) to +100 (good). Actions shift the needle. NPCs react to your moral standing. Certain quests and endings require specific alignment thresholds." },
        { value: "dual_axis", label: "Dual Axis (good/evil + lawful/chaotic)", description: "Two axes: good/evil (-100 to +100) and lawful/chaotic (-100 to +100). Creates 9 alignment categories (Lawful Good, Chaotic Evil, etc.). More nuanced than spectrum." },
        { value: "grey", label: "Grey (no clear good/evil, only consequences)", description: "No objective morality. Every choice has trade-offs. The system tracks moral weight for consequence severity but never labels the player as 'good' or 'evil'. Best for mature narratives." },
        { value: "faction_morality", label: "Faction-relative (each faction has its own moral code)", description: "Morality is relative — each faction defines virtuous differently. Helping one faction may be seen as evil by another. The player must navigate conflicting moral codes." },
      ],
      default: "grey",
    },
    {
      key: "visible",
      label: "Visible to Player",
      type: "boolean",
      default: false,
      description: "Show alignment score/status to the player. When hidden, alignment is tracked internally and expressed through NPC reactions and story consequences.",
    },
  ],
  systemPromptSection: (config) => {
    const system = config.system || "grey";
    const visible = config.visible || false;
    return `## MORALITY (Guideline — Mandatory Post-Turn + Event-Reactive)
- System: ${system}. Visible: ${visible ? "yes" : "hidden"}
- ${system === "spectrum" ? "Track moral alignment on a spectrum (-100 evil to +100 good). Actions shift the needle." : system === "dual_axis" ? "Track two axes: good/evil and lawful/chaotic (-100 to +100 each). Creates alignment categories." : system === "grey" ? "No objective morality. Every choice has trade-offs. Track moral weight for consequence severity but never label the player." : "Each faction defines its own morality. Track alignment relative to each faction's moral framework."}
- ${visible ? "Report alignment shifts to the player when significant choices occur." : "Track internally. Express alignment through NPC reactions, story consequences, and available options."}
- MANDATORY: The onEvent handler automatically adjusts alignment when killing innocents/surrendering NPCs or completing quests with moral weight.
- MANDATORY: The postTurnHook reminds you of the player's current moral standing so NPCs can react appropriately.
- The Director should weight consequence severity based on moral implications. Moral dilemmas should be flagged.
- Use update_alignment tool after significant moral choices.
- Present genuine moral dilemmas — not 'help the orphan or kick the puppy.'`;
  },
  widgets: [
    {
      id: "morality_status",
      type: "status_bar_item",
      label: "Alignment",
      icon: "Scale",
      component: "MoralityStatus",
      dataBindings: {
        alignment_label: "variables.alignment_label",
        visible: "variables._morality_visible",
      },
      visibility: "when_data",
      priority: 60,
    },
    {
      id: "morality_panel",
      type: "sidebar_tab",
      label: "Morality",
      icon: "Scale",
      component: "MoralityWidget",
      dataBindings: {
        alignment_label: "variables.alignment_label",
        alignment_score: "variables.alignment_score",
        lawful_score: "variables.lawful_score",
        system: "variables._morality_system",
        visible: "variables._morality_visible",
        recent_shifts: "variables._morality_shifts",
      },
      visibility: "when_data",
      priority: 55,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "update_alignment",
        description: "Update the player's moral alignment after a significant choice.",
        parameters: {
          type: "object",
          properties: {
            alignment_label: { type: "string", description: "New alignment label (e.g., 'Lawful Good', 'Neutral')" },
            alignment_score: { type: "number", description: "Good/evil score (-100 to +100)" },
            lawful_score: { type: "number", description: "Lawful/chaotic score (-100 to +100), for dual_axis only" },
            reason: { type: "string", description: "What caused the shift" },
            direction: { type: "string", description: "Which way it shifted: good, evil, lawful, chaotic" },
            magnitude: { type: "number", description: "How much it shifted (1-20)" },
          },
          required: ["alignment_label", "reason"],
        },
      },
    },
  ],
  initializeState: (config) => ({
    alignment_label: "",
    alignment_score: 0,
    lawful_score: 0,
    _morality_system: config.system || "grey",
    _morality_visible: config.visible || false,
    _morality_shifts: [],
  }),
  onEvent: (event) => {
    if (event.type === "combat_kill" && (event.data.was_innocent || event.data.was_surrendering)) {
      return {
        stateUpdates: { _alignment_shift_pending: -10 },
        narrativeNotes: [`⚖ Moral shift: killing ${event.data.target} darkens alignment.`],
      };
    }
    if (event.type === "quest_complete" && event.data.moral_weight) {
      return {
        stateUpdates: { _alignment_shift_pending: event.data.moral_weight },
        narrativeNotes: [`⚖ Quest moral weight: ${event.data.moral_weight > 0 ? "good" : "evil"} +${Math.abs(event.data.moral_weight)}.`],
      };
    }
    return null;
  },
  postTurnHook: (context) => {
    const vars = context.state.variables || {};
    const score = vars.alignment_score || 0;
    const shifts = vars._morality_shifts || [];
    const recentShifts = shifts.filter((s: any) => ((vars._current_turn || 0) - (s.turn || 0)) <= 3);
    
    if (recentShifts.length > 0 && context.config.visible === false) {
      return {
        narrativeNotes: [`⚖ Morality: score ${score}. NPCs should react to the player's ${score > 20 ? "virtuous" : score < -20 ? "dark" : "ambiguous"} tendencies.`],
      };
    }
    return null;
  },
};
